'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  FiRefreshCw, 
  FiCheckCircle, 
  FiXCircle, 
  FiSmartphone, 
  FiCopy, 
  FiCheck,
  FiInfo,
  FiLayers,
  FiCode,
  FiMessageSquare,
  FiZap,
  FiTerminal,
  FiSmartphone as FiPhoneIcon,
  FiSearch,
  FiClock,
  FiSend,
  FiFile
} from 'react-icons/fi'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/ui-dialog'
import QRCode from 'react-qr-code'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

export default function WhatsAppAPIPage() {
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [fetchingQR, setFetchingQR] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [tokenLoading, setTokenLoading] = useState(true)
  const [tokenCanGenerate, setTokenCanGenerate] = useState(true)
  const [tokenMessage, setTokenMessage] = useState('')
  const [copying, setCopying] = useState(false)

  // Estados de la Terminal de Pruebas
  const [activeTab, setActiveTab] = useState('text')
  const [terminalPhone, setTerminalPhone] = useState('')
  const [terminalMessage, setTerminalMessage] = useState('')
  const [terminalFileUrl, setTerminalFileUrl] = useState('')
  const [terminalFile, setTerminalFile] = useState<File | null>(null)
  const [uploadMethod, setUploadMethod] = useState<'url' | 'file'>('url')
  const [extraParams, setExtraParams] = useState('')
  const [terminalCaption, setTerminalCaption] = useState('')
  const [terminalLoading, setTerminalLoading] = useState(false)
  const [terminalResponse, setTerminalResponse] = useState<any>(null)
  const [terminalResponseTime, setTerminalResponseTime] = useState<string | null>(null)
  
  // Estados de progreso de vinculación
  const [connectionProgress, setConnectionProgress] = useState(0)
  const [connectionStatusMessage, setConnectionStatusMessage] = useState('')
  
  // Estado del modal de documentación
  const [showDocsModal, setShowDocsModal] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Asegurar que el Dialog solo se renderice en el cliente
  useEffect(() => {
    setMounted(true)
  }, [])

  // Cargar token del usuario
  useEffect(() => {
    const fetchToken = async () => {
      setTokenLoading(true)
      try {
        const res = await fetch('/api/user/token', { cache: 'no-store' })
        const data = await res.json()

        if (res.ok && data.success) {
          setToken(data.token ?? null)
          setTokenCanGenerate(data.canGenerate !== false)
          setTokenMessage(
            data.message ||
            (data.token ? '' : 'No tienes un token activo para consumir la API de WhatsApp.')
          )
        } else {
          setToken(null)
          setTokenCanGenerate(false)
          setTokenMessage(data.message || data.error || 'No pudimos obtener tu token de acceso.')
        }
      } catch (e) {
        console.error('Error cargando token:', e)
        setToken(null)
        setTokenCanGenerate(false)
        setTokenMessage('No pudimos cargar tu token de acceso en este momento.')
      } finally {
        setTokenLoading(false)
      }
    }
    fetchToken()
  }, [])

  const checkStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/whatsapp/status')
      
      // Verificar si la respuesta es JSON antes de parsear
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error('El servidor no devolvió JSON');
        setIsConnected(false);
        return;
      }

      const data = await res.json()
      if (data.success) {
        setIsConnected(data.connected)
        setConnectionProgress(data.progress || 0)
        setConnectionStatusMessage(data.statusMessage || '')
        if (data.connected) {
          setQrCode(null)
          setConnectionProgress(100)
        }
      } else {
        setIsConnected(false)
      }
    } catch (e) {
      console.error('Error al verificar estado:', e)
      setIsConnected(false)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchQR = useCallback(async () => {
    if (fetchingQR) return
    setFetchingQR(true)
    try {
      const res = await fetch('/api/whatsapp/qr')
      const contentType = res.headers.get("content-type")
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error('Respuesta no válida')
      }
      const data = await res.json()
      if (data.success && data.qr) {
        setQrCode(data.qr)
      } else if (data.connected) {
        setIsConnected(true)
        setQrCode(null)
      }
    } catch (e) {
      console.error('Error al obtener QR:', e)
    } finally {
      setFetchingQR(false)
    }
  }, [fetchingQR])

  const handleReset = async () => {
    const result = await Swal.fire({
      title: '¿Reiniciar conexión?',
      text: 'Esto cerrará la sesión actual y permitirá generar un nuevo QR.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, reiniciar',
      cancelButtonText: 'Cancelar'
    })

    if (result.isConfirmed) {
      setFetchingQR(true)
      try {
        const res = await fetch('/api/whatsapp/reset', { method: 'POST' })
        const data = await res.json()
        if (data.success) {
          toast.success('Servicio reiniciado')
          setQrCode(null)
          setIsConnected(false)
          setTimeout(fetchQR, 1000)
        } else {
          toast.error(data.message || 'Error al reiniciar')
        }
      } catch (e) {
        console.error(e)
        toast.error('Error de red')
      } finally {
        setFetchingQR(false)
      }
    }
  }

  // Conexión en Tiempo Real vía SSE (Server-Sent Events)
  useEffect(() => {
    let eventSource: EventSource | null = null;

    const connectSSE = () => {
      console.log('📡 Conectando a eventos en tiempo real...');
      eventSource = new EventSource('/api/whatsapp/events');

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Update de WhatsApp recibido:', data);
          setIsConnected(data.connected);
          setIsReconnecting(data.isReconnecting || false);
          setQrCode(data.qr);
          setConnectionProgress(data.progress || 0);
          setConnectionStatusMessage(data.statusMessage || '');
          
          if (data.connected) {
            setQrCode(null);
            setConnectionProgress(100);
            setIsReconnecting(false);
          }
          setLoading(false);
        } catch (e) {
          console.error('Error parseando SSE:', e);
        }
      };

      eventSource.onerror = (err) => {
        console.error('Error en SSE:', err);
        eventSource?.close();
        // Reintentar conexión en 5 segundos si falla
        setTimeout(connectSSE, 5000);
      };
    };

    connectSSE();

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  const copyToClipboard = () => {
    if (!token) return
    navigator.clipboard.writeText(token)
    setCopying(true)
    toast.success('Token copiado al portapapeles')
    setTimeout(() => setCopying(false), 2000)
  }

  const handleTerminalTest = async () => {
    if (!token) {
      return Swal.fire('Error', tokenMessage || 'No se encontró un token de acceso', 'error')
    }
    if (!terminalPhone) {
      return Swal.fire('Error', 'Ingresa un número de teléfono', 'error')
    }

    setTerminalLoading(true)
    setTerminalResponse(null)
    setTerminalResponseTime(null)

    const startTime = performance.now()
    const endpoint = activeTab === 'text' ? '/api/whatsapp/send' : '/api/whatsapp/send-file'
    
    // Parsear parámetros extra
    let parsedExtras = {}
    if (extraParams.trim()) {
      try {
        parsedExtras = JSON.parse(extraParams)
      } catch (e) {
        setTerminalLoading(false)
        return Swal.fire('Error de JSON', 'Los parámetros extra no son un JSON válido', 'error')
      }
    }

    try {
      let res;
      if (activeTab === 'file' && uploadMethod === 'file' && terminalFile) {
        // Enviar como FormData
        const formData = new FormData()
        formData.append('phone', terminalPhone)
        formData.append('file', terminalFile)
        if (terminalCaption) formData.append('caption', terminalCaption)
        
        // Añadir extras al FormData si existen
        Object.entries(parsedExtras).forEach(([key, value]) => {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
        })

        res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        })
      } else {
        // Enviar como JSON
        const body = activeTab === 'text' 
          ? { phone: terminalPhone, message: terminalMessage, ...parsedExtras }
          : { phone: terminalPhone, fileUrl: terminalFileUrl, caption: terminalCaption, ...parsedExtras }

        res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        })
      }

      const data = await res.json()
      const endTime = performance.now()
      const timeSecs = (endTime - startTime) / 1000
      setTerminalResponseTime(timeSecs < 1 ? `${(timeSecs * 1000).toFixed(0)}ms` : `${timeSecs.toFixed(2)}s`)
      setTerminalResponse(data)

      if (res.ok) {
        toast.success('Prueba enviada exitosamente')
      } else {
        toast.error(data.error || 'Error al enviar prueba')
      }
    } catch (e: any) {
      setTerminalResponse({ error: 'Error al conectar con la API', details: e.message })
    } finally {
      setTerminalLoading(false)
    }
  }

  const copyTerminalResponse = () => {
    if (!terminalResponse) return
    navigator.clipboard.writeText(JSON.stringify(terminalResponse, null, 2))
    toast.success('Respuesta copiada')
  }

  if (!mounted) {
    return (
      <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12">
        <div className="h-32 bg-gray-100 rounded-[2.5rem] animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="h-64 bg-gray-100 rounded-[2.5rem] animate-pulse" />
            <div className="h-64 bg-gray-100 rounded-[2.5rem] animate-pulse" />
          </div>
          <div className="h-96 bg-gray-100 rounded-[2.5rem] animate-pulse" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12">
      {/* Header Seccion */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-xl">
              <FiMessageSquare className="text-green-600 h-8 w-8" />
            </div>
            API de WhatsApp
          </h1>
          <p className="text-gray-500 mt-2 text-lg">
            Conecta tu cuenta y automatiza el envío de mensajes y archivos.
          </p>
        </div>
        <div className="flex gap-3">
          {mounted && (
            <Dialog open={showDocsModal} onOpenChange={setShowDocsModal}>
              <DialogTrigger asChild>
                <Button 
                  variant="default" 
                  className="rounded-xl bg-blue-600 hover:bg-blue-700 h-12 px-6"
                >
                  <FiInfo className="mr-2 h-5 w-5" />
                  Documentación API
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FiCode className="text-blue-600 h-6 w-6" />
                    </div>
                    Documentación de la API de WhatsApp
                  </DialogTitle>
                </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Introducción */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">📋 Introducción</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Esta API te permite enviar mensajes de texto y archivos a través de WhatsApp de forma programática. 
                    Utiliza autenticación mediante Bearer Token y soporta múltiples formatos de archivo.
                  </p>
                </div>

                {/* URL Base */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">🌐 URL Base de Consumo</h3>
                  <p className="text-gray-600">Todas las peticiones deben usar esta URL base:</p>
                  <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-xl p-5 border-2 border-blue-500">
                    <code className="text-lg font-mono text-white font-bold">https://admin.miapi.cloud</code>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <strong>💡 Ejemplo:</strong> Para enviar un mensaje, la URL completa sería: <code className="bg-blue-100 px-2 py-1 rounded">https://admin.miapi.cloud/api/whatsapp/send</code>
                  </div>
                </div>

                {/* Autenticación */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">🔐 Autenticación</h3>
                  <p className="text-gray-600">Todas las peticiones requieren un Bearer Token en el header:</p>
                  <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm text-green-400">
                    <code>Authorization: Bearer {token ? token.substring(0, 20) + '...' : 'TU_TOKEN_AQUI'}</code>
                  </div>
                </div>



                {/* Endpoints */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">🌐 Endpoints Disponibles</h3>
                  
                  {/* Endpoint 1: Enviar Mensaje de Texto */}
                  <div className="border border-gray-200 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-600">POST</Badge>
                      <code className="text-sm font-mono text-gray-800">/api/whatsapp/send</code>
                    </div>
                    <p className="text-sm text-gray-600">Envía un mensaje de texto a un número de WhatsApp.</p>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-500 uppercase">Body (JSON):</p>
                      <div className="bg-slate-900 rounded-lg p-3 font-mono text-xs text-green-400 overflow-x-auto">
                        <pre>{`{
  "phone": "51987654321",
  "message": "Hola, este es un mensaje de prueba"
}`}</pre>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-500 uppercase">Respuesta Exitosa:</p>
                      <div className="bg-slate-900 rounded-lg p-3 font-mono text-xs text-green-400 overflow-x-auto">
                        <pre>{`{
  "success": true,
  "data": { ... },
  "consultasRestantes": 950
}`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Endpoint 2: Enviar Archivo */}
                  <div className="border border-gray-200 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-600">POST</Badge>
                      <code className="text-sm font-mono text-gray-800">/api/whatsapp/send-file</code>
                    </div>
                    <p className="text-sm text-gray-600">Envía un archivo (imagen, PDF, video, etc.) a un número de WhatsApp.</p>
                    
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-500 uppercase">Opción 1 - Body (JSON con URL):</p>
                      <div className="bg-slate-900 rounded-lg p-3 font-mono text-xs text-green-400 overflow-x-auto">
                        <pre>{`{
  "phone": "51987654321",
  "fileUrl": "https://ejemplo.com/archivo.pdf",
  "caption": "Aquí está tu documento" // Opcional
}`}</pre>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-500 uppercase">Opción 2 - FormData (Subir archivo):</p>
                      <div className="bg-slate-900 rounded-lg p-3 font-mono text-xs text-green-400 overflow-x-auto">
                        <pre>{`const formData = new FormData();
formData.append('phone', '51987654321');
formData.append('file', archivoBlob);
formData.append('caption', 'Texto opcional');

fetch('/api/whatsapp/send-file', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer TU_TOKEN'
  },
  body: formData
})`}</pre>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                      <strong>⚠️ Límite:</strong> Tamaño máximo de archivo: 16MB
                    </div>
                  </div>

                  {/* Endpoint 3: Obtener QR */}
                  <div className="border border-gray-200 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-600">GET</Badge>
                      <code className="text-sm font-mono text-gray-800">/api/whatsapp/qr</code>
                    </div>
                    <p className="text-sm text-gray-600">Obtiene el código QR para vincular WhatsApp (uso interno).</p>
                  </div>

                  {/* Endpoint 4: Estado de Conexión */}
                  <div className="border border-gray-200 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-indigo-600">GET</Badge>
                      <code className="text-sm font-mono text-gray-800">/api/whatsapp/status</code>
                    </div>
                    <p className="text-sm text-gray-600">Verifica el estado de la conexión de WhatsApp.</p>
                  </div>
                </div>

                {/* Formato de Número */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">📱 Formato de Número</h3>
                  <p className="text-gray-600">Los números deben incluir el código de país sin el símbolo +:</p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <FiCheck className="text-green-600" />
                      <code className="text-sm font-mono">51987654321</code>
                      <span className="text-xs text-gray-500">(Perú)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiCheck className="text-green-600" />
                      <code className="text-sm font-mono">5491112345678</code>
                      <span className="text-xs text-gray-500">(Argentina)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiXCircle className="text-red-600" />
                      <code className="text-sm font-mono line-through">+51987654321</code>
                      <span className="text-xs text-red-500">(Incorrecto)</span>
                    </div>
                  </div>
                </div>

                {/* Códigos de Error */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">⚠️ Códigos de Error Comunes</h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <Badge variant="outline" className="bg-white">401</Badge>
                      <div>
                        <p className="text-sm font-bold text-red-900">Token inválido o no proporcionado</p>
                        <p className="text-xs text-red-700">Verifica que el Bearer Token sea correcto</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <Badge variant="outline" className="bg-white">429</Badge>
                      <div>
                        <p className="text-sm font-bold text-orange-900">Límite de consultas alcanzado</p>
                        <p className="text-xs text-orange-700">Has superado tu cuota mensual de mensajes</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <Badge variant="outline" className="bg-white">400</Badge>
                      <div>
                        <p className="text-sm font-bold text-yellow-900">Parámetros faltantes o inválidos</p>
                        <p className="text-xs text-yellow-700">Revisa que phone y message/fileUrl estén presentes</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ejemplo Completo */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">💻 Ejemplo Completo (JavaScript)</h3>
                  <div className="bg-slate-900 rounded-xl p-4 font-mono text-xs text-green-400 overflow-x-auto">
                    <pre>{`// Enviar mensaje de texto
const enviarMensaje = async () => {
  const response = await fetch('https://admin.miapi.cloud/api/whatsapp/send', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ${token ? token.substring(0, 20) + '...' : 'TU_TOKEN'}',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      phone: '51987654321',
      message: 'Hola desde mi aplicación'
    })
  });
  
  const data = await response.json();
  console.log(data);
};

// Enviar archivo desde URL
const enviarArchivo = async () => {
  const response = await fetch('https://admin.miapi.cloud/api/whatsapp/send-file', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer ${token ? token.substring(0, 20) + '...' : 'TU_TOKEN'}',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      phone: '51987654321',
      fileUrl: 'https://ejemplo.com/documento.pdf',
      caption: 'Adjunto el documento solicitado'
    })
  });
  
  const data = await response.json();
  console.log(data);
};`}</pre>
                  </div>
                </div>

                {/* Mejores Prácticas */}
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-gray-900">✨ Mejores Prácticas</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex gap-2">
                      <FiCheckCircle className="text-green-600 mt-0.5 shrink-0" />
                      <span>Mantén tu token seguro y nunca lo expongas en el código del cliente</span>
                    </li>
                    <li className="flex gap-2">
                      <FiCheckCircle className="text-green-600 mt-0.5 shrink-0" />
                      <span>Implementa manejo de errores robusto para todas las peticiones</span>
                    </li>
                    <li className="flex gap-2">
                      <FiCheckCircle className="text-green-600 mt-0.5 shrink-0" />
                      <span>Verifica el estado de conexión antes de enviar mensajes masivos</span>
                    </li>
                    <li className="flex gap-2">
                      <FiCheckCircle className="text-green-600 mt-0.5 shrink-0" />
                      <span>Respeta los límites de consultas mensuales de tu plan</span>
                    </li>
                    <li className="flex gap-2">
                      <FiCheckCircle className="text-green-600 mt-0.5 shrink-0" />
                      <span>Para archivos grandes, considera usar URLs públicas en lugar de FormData</span>
                    </li>
                  </ul>
                </div>

                {/* Soporte */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                  <h3 className="text-lg font-bold text-blue-900 mb-2">💬 ¿Necesitas Ayuda?</h3>
                  <p className="text-sm text-blue-800">
                    Si tienes problemas con la integración, verifica primero que tu instancia de WhatsApp esté conectada 
                    en la sección superior de esta página. Todos los mensajes enviados quedan registrados en el historial 
                    de consumo para auditoría.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          )}
          
          <Button 
            onClick={() => {
              setLoading(true)
              checkStatus()
            }} 
            variant="outline" 
            disabled={loading}
            className="rounded-xl border-2 hover:bg-gray-50 h-12 px-6"
          >
            <FiRefreshCw className={`mr-2 h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            Actualizar Estado
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Columna Izquierda: Estado y Token */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Tarjeta de Estado Premium */}
          <Card className="border-none shadow-2xl shadow-gray-200/50 overflow-hidden bg-white group transition-all">
            <div className={`h-2 w-full ${isConnected ? 'bg-green-500' : (isReconnecting ? 'bg-amber-500' : 'bg-red-500')} transition-colors animate-pulse`} />
            <CardContent className="p-8">
              <div className="flex flex-col sm:flex-row items-center gap-8">
                <div className={`h-24 w-24 rounded-[2rem] flex items-center justify-center shrink-0 border-4 ${
                  isConnected 
                    ? 'bg-green-50 border-green-100 text-green-600' 
                    : (isReconnecting ? 'bg-amber-50 border-amber-100 text-amber-600' : 'bg-red-50 border-red-100 text-red-600')
                } transition-all group-hover:scale-105 duration-300`}>
                  {isConnected 
                    ? <FiCheckCircle className="h-12 w-12" /> 
                    : (isReconnecting ? <FiRefreshCw className="h-12 w-12 animate-spin" /> : <FiXCircle className="h-12 w-12" />)
                  }
                </div>
                <div className="text-center sm:text-left flex-1">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                    <h2 className="text-2xl font-bold text-gray-900">Estado del Servicio</h2>
                    <Badge className={`px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                      isConnected 
                        ? 'bg-green-500 text-white' 
                        : (isReconnecting ? 'bg-amber-500 text-white' : 'bg-red-500 text-white')
                    }`}>
                      {isConnected ? 'Conectado' : (isReconnecting ? 'Reconectando' : 'Desconectado')}
                    </Badge>
                  </div>
                  <p className="text-gray-500 mt-2 leading-relaxed">
                    {isConnected 
                      ? 'Tu cuenta de WhatsApp está vinculada correctamente. Puedes empezar a consumir la API.' 
                      : (isReconnecting 
                          ? 'Hubo una micro-desconexión de red. Estamos restableciendo el servicio automáticamente...' 
                          : 'El servicio está esperando la vinculación. Genera un código QR y escanéalo con tu dispositivo.')}
                  </p>
                  
                  {isConnected && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-100 rounded-2xl flex items-center gap-3 text-green-800 text-sm">
                      <FiLayers className="h-5 w-5" />
                      Instancia lista y optimizada para envíos masivos.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tarjeta de Token (Diseño Mi Token) */}
          <Card className="border-none shadow-xl border border-gray-100 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
              <CardTitle className="flex items-center gap-3">
                <FiCode className="h-6 w-6" />
                Tu Bearer Token de Acceso
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8 space-y-6">
               <p className="text-gray-600 leading-relaxed">
                Utiliza este token único para autenticar tus peticiones a la API de WhatsApp.
              </p>
              
              <div className="relative group">
                <div className="bg-slate-900 border-2 border-slate-800 rounded-2xl p-6 font-mono text-sm text-green-400 break-all select-all min-h-[100px] flex items-center justify-center transition-all group-hover:border-blue-500 shadow-inner">
                  {tokenLoading ? (
                    <FiRefreshCw className="animate-spin h-6 w-6 text-blue-500" />
                  ) : token ? (
                    token
                  ) : (
                    <div className="text-center space-y-2">
                      <p className="text-sm font-semibold text-amber-300">Token no disponible</p>
                      <p className="text-xs text-slate-300">{tokenMessage || 'No tienes un token API activo.'}</p>
                    </div>
                  )}
                </div>
                
                <button 
                  onClick={copyToClipboard}
                  disabled={!token}
                  className="absolute -bottom-4 right-6 bg-white border-2 border-blue-100 shadow-xl rounded-full p-4 hover:scale-110 transition-transform active:scale-95 text-blue-600 group/btn"
                  title="Copiar Token"
                >
                  {copying ? <FiCheck className="h-6 w-6 text-green-500 animate-in zoom-in" /> : <FiCopy className="h-6 w-6 group-hover/btn:rotate-12 transition-transform" />}
                </button>
              </div>

              <div className="flex gap-4 pt-4">
                <div className="flex-1 p-4 bg-blue-50 rounded-2xl border border-blue-100 flex gap-3 italic text-xs text-blue-800">
                  <FiInfo className="h-4 w-4 shrink-0 mt-0.5" />
                  Este token es el mismo que usas para los servicios de validación y facturación.
                </div>
              </div>

              {!tokenLoading && !token && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <p className="text-sm text-amber-900">
                    {tokenMessage || 'Necesitas un token activo para probar los endpoints de WhatsApp.'}
                  </p>
                  {tokenCanGenerate && (
                    <Link
                      href="/token"
                      className="inline-flex items-center justify-center rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                    >
                      Generar Token
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Columna Derecha: QR Code */}
        <div className="lg:col-span-1">
          {!isConnected ? (
            <Card className="border-none shadow-2xl shadow-blue-200/20 h-full overflow-hidden border border-blue-50 border-t-4 border-t-blue-500">
              <CardHeader>
                 <CardTitle className="text-lg font-bold flex items-center gap-2">
                  <FiSmartphone className="text-blue-500" /> Vincular Dispositivo
                 </CardTitle>
              </CardHeader>
              <CardContent className="p-8 flex flex-col items-center justify-center space-y-8">
                {qrCode ? (
                  <>
                    <div className="bg-white p-6 rounded-3xl border-4 border-dashed border-gray-100 shadow-inner group transition-all hover:border-blue-400">
                      <QRCode value={qrCode} size={200} />
                    </div>
                    <div className="text-center space-y-4 w-full">
                      <p className="font-bold text-gray-900 text-lg">
                        {connectionProgress > 25 ? connectionStatusMessage : 'Escanea el código'}
                      </p>
                      
                      {/* Barra de Progreso */}
                      {connectionProgress > 0 && (
                        <div className="space-y-2 px-4">
                          <div className="flex justify-between text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                            <span>Progreso de Vinculación</span>
                            <span>{connectionProgress}%</span>
                          </div>
                          <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 transition-all duration-500 ease-out"
                              style={{ width: `${connectionProgress}%` }}
                            />
                          </div>
                          {connectionProgress > 50 && connectionProgress < 100 && (
                             <p className="text-[10px] text-orange-500 font-medium animate-pulse">
                               Sincronizando... No cierres esta pestaña
                             </p>
                          )}
                        </div>
                      )}

                      <ul className="text-sm text-gray-500 text-left space-y-3 px-4">
                        <li className="flex gap-3">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">1</span>
                          Abre WhatsApp en tu teléfono
                        </li>
                        <li className="flex gap-3">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">2</span>
                          Ve a Dispositivos vinculados
                        </li>
                        <li className="flex gap-3">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold">3</span>
                          Toca "Vincular" y apunta al QR
                        </li>
                      </ul>
                    </div>
                    <div className="w-full space-y-3">
                      <Button onClick={fetchQR} variant="ghost" disabled={fetchingQR} className="text-blue-600 hover:bg-blue-50 w-full rounded-xl">
                        Regenerar Código QR
                      </Button>
                      <Button onClick={handleReset} variant="outline" className="w-full border-red-50 text-red-400 hover:bg-red-50 hover:text-red-500 rounded-xl text-xs">
                        Reiniciar Sesión Completa
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="py-12 flex flex-col items-center text-center gap-6">
                    <div className="h-20 w-20 bg-blue-50 rounded-full flex items-center justify-center">
                      <FiPhoneIcon className="h-10 w-10 text-blue-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">¿Listo para conectar?</h3>
                      <p className="text-sm text-gray-500 mt-2 px-6">Genera un código QR único para vincular tu instancia rápidamente.</p>
                    </div>
                    <Button 
                      onClick={fetchQR} 
                      disabled={fetchingQR}
                      className="bg-blue-600 hover:bg-blue-700 w-full py-8 text-lg font-bold rounded-2xl shadow-xl shadow-blue-200"
                    >
                      {fetchingQR ? <FiRefreshCw className="animate-spin mr-2" /> : <FiZap className="mr-2" />}
                      {fetchingQR ? 'Generando...' : 'Generar QR Ahora'}
                    </Button>
                    <Button onClick={handleReset} variant="ghost" className="text-[10px] text-gray-400 hover:text-red-500 w-full h-auto py-0">
                      Reiniciar Sesión Completa
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-none shadow-xl bg-gradient-to-br from-green-50 to-white h-full border border-green-100">
               <CardContent className="p-8 flex flex-col items-center justify-center h-full space-y-6 text-center">
                  <div className="h-24 w-24 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-200">
                    <FiCheckCircle className="h-12 w-12 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">¡Conexión Activa!</h3>
                    <p className="text-sm text-gray-500 mt-1">Tu instancia está lista para enviar mensajes.</p>
                  </div>
                  <div className="w-full pt-4 space-y-3">
                    <div className="p-4 bg-green-50 rounded-2xl border border-green-100 flex items-center justify-between">
                      <span className="text-xs font-medium text-green-700">Estado del Servicio</span>
                      <Badge className="bg-green-500">OPERATIVO</Badge>
                    </div>

                    {connectionProgress < 100 && (
                      <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex flex-col gap-2">
                         <div className="flex items-center justify-between text-[10px] font-bold text-blue-600 uppercase">
                           <span>Sincronizando Historial</span>
                           <span>{connectionProgress}%</span>
                         </div>
                         <div className="h-1.5 w-full bg-blue-100 rounded-full overflow-hidden">
                           <div 
                             className="h-full bg-blue-500 animate-pulse transition-all duration-500"
                             style={{ width: `${connectionProgress}%` }}
                           />
                         </div>
                         <p className="text-[10px] text-blue-500 text-left italic">
                            Estamos cargando tus chats. Esto puede tardar unos minutos.
                         </p>
                      </div>
                    )}
                    
                    <Button 
                      variant="outline" 
                      onClick={handleReset}
                      className="w-full border-red-100 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl"
                    >
                      Reiniciar Sesión Completa
                    </Button>
                  </div>
               </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Terminal de Pruebas Interactiva */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FiTerminal className="text-blue-600 h-6 w-6" /> Terminal de Pruebas Interactiva
        </h3>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-100 p-1 rounded-xl mb-6 flex h-auto gap-1 max-w-sm">
            <TabsTrigger value="text" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center gap-2 text-sm">
              <FiMessageSquare className="h-4 w-4" /> Texto
            </TabsTrigger>
            <TabsTrigger value="file" className="flex-1 rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center justify-center gap-2 text-sm">
              <FiFile className="h-4 w-4" /> Archivo
            </TabsTrigger>
          </TabsList>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Formulario de Entrada */}
            <Card className="border-none shadow-xl border border-gray-100">
              <CardHeader className="bg-gray-50/50 border-b py-4">
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <FiSend className="text-blue-600" /> Configurar Solicitud
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Número de WhatsApp</Label>
                  <div className="flex">
                    <span className="flex px-4 items-center bg-gray-100 border border-gray-200 border-r-0 rounded-l-xl text-sm text-gray-500 font-bold">+</span>
                    <Input 
                      placeholder="51987654321" 
                      value={terminalPhone}
                      onChange={(e) => setTerminalPhone(e.target.value)}
                      className="rounded-l-none rounded-r-xl border-gray-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <TabsContent value="text" className="space-y-6 mt-0">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest flex justify-between">
                      <span>Mensaje de Texto</span>
                      <span className="text-[10px] lowercase font-normal italic">Soporta *negrita*, _cursiva_, ~tachado~</span>
                    </Label>
                    <textarea 
                      placeholder="Hola, este es un mensaje de prueba..." 
                      rows={4}
                      value={terminalMessage}
                      onChange={(e) => setTerminalMessage(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm leading-relaxed"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="file" className="space-y-6 mt-0">
                  <div className="space-y-3">
                    <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Método de Envío</Label>
                    <div className="flex bg-gray-100 p-1 rounded-xl gap-1">
                      <button 
                        onClick={() => setUploadMethod('url')}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${uploadMethod === 'url' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        URL Pública
                      </button>
                      <button 
                        onClick={() => setUploadMethod('file')}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${uploadMethod === 'file' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        Subir Archivo
                      </button>
                    </div>
                  </div>

                  {uploadMethod === 'url' ? (
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest">URL del Archivo (PDF, JPG, PNG)</Label>
                      <Input 
                        placeholder="https://tusitio.com/comprobante.pdf" 
                        value={terminalFileUrl}
                        onChange={(e) => setTerminalFileUrl(e.target.value)}
                        className="rounded-xl border-gray-200 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Seleccionar Archivo</Label>
                      <div className="relative group">
                        <input 
                          type="file" 
                          id="file-upload"
                          className="hidden"
                          onChange={(e) => setTerminalFile(e.target.files?.[0] || null)}
                        />
                        <label 
                          htmlFor="file-upload"
                          className="flex flex-col items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-200 border-dashed rounded-xl appearance-none cursor-pointer hover:border-blue-400 focus:outline-none group-hover:bg-blue-50/30"
                        >
                          <span className="flex items-center space-x-2">
                             <FiFile className={`w-6 h-6 ${terminalFile ? 'text-green-500' : 'text-gray-400'}`} />
                             <span className="text-sm font-medium text-gray-600 truncate max-w-[200px]">
                               {terminalFile ? terminalFile.name : 'Haz clic para seleccionar'}
                             </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Leyenda (Caption)</Label>
                    <Input 
                      placeholder="Opcional: Adjunto mi comprobante" 
                      value={terminalCaption}
                      onChange={(e) => setTerminalCaption(e.target.value)}
                      className="rounded-xl border-gray-200 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </TabsContent>

                <div className="space-y-2 pt-2">
                  <Label className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    Parámetros Extra (JSON)
                    <Badge variant="outline" className="text-[9px] py-0 font-normal">Opcional</Badge>
                  </Label>
                  <textarea 
                    placeholder='{ "ruc": "20601234567", "dni": "45678912" }' 
                    rows={3}
                    value={extraParams}
                    onChange={(e) => setExtraParams(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono text-xs"
                  />
                  <p className="text-[10px] text-gray-400 italic">Estos parámetros se enviarán en el cuerpo de la solicitud.</p>
                </div>

                <Button 
                  onClick={handleTerminalTest}
                  disabled={terminalLoading || !isConnected || !token}
                  className={`w-full py-7 text-lg font-bold rounded-2xl shadow-xl transition-all ${isConnected && token ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' : 'bg-gray-400 cursor-not-allowed'}`}
                >
                  {terminalLoading ? <FiRefreshCw className="animate-spin mr-2" /> : <FiZap className="mr-2" />}
                  {terminalLoading ? 'Procesando...' : 'Probar Envío'}
                </Button>

                {(!isConnected || !token) && (
                  <p className="text-center text-xs text-red-500 font-medium">
                    {!token
                      ? (tokenMessage || 'Necesitas un token activo para usar la terminal')
                      : 'Debes vincular WhatsApp para usar la terminal'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Consola de Respuesta */}
            <Card className="border-none shadow-xl bg-[#1e1e1e] overflow-hidden flex flex-col min-h-[400px]">
              <CardHeader className="border-b border-gray-800 py-4 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-300">
                  <FiCode className="text-purple-400" /> Respuesta JSON
                </CardTitle>
                <div className="flex items-center gap-3">
                  {terminalResponseTime && (
                    <span className="text-[10px] font-bold text-gray-500 bg-black/30 px-2 py-1 rounded-md border border-gray-800 flex items-center gap-1">
                      <FiClock className="h-3 w-3" /> {terminalResponseTime}
                    </span>
                  )}
                  <button onClick={copyTerminalResponse} className="text-gray-500 hover:text-white transition-colors">
                    <FiCopy className="h-4 w-4" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-6 font-mono text-sm overflow-auto text-green-400 scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
                {terminalResponse ? (
                  <pre className="whitespace-pre-wrap">{JSON.stringify(terminalResponse, null, 2)}</pre>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-600 space-y-4">
                    <FiTerminal className="h-16 w-16 opacity-10" />
                    <p className="text-[10px] uppercase tracking-[0.3em] font-medium">Esperando solicitud...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </Tabs>
      </div>

      {/* Footer Info */}
      <div className="bg-amber-50 border border-amber-100 p-6 rounded-[2rem] flex gap-4">
        <div className="p-3 bg-white rounded-xl shadow-sm h-fit">
            <FiInfo className="text-amber-600 h-6 w-6" />
        </div>
        <div>
            <h4 className="font-bold text-amber-900">Nota Ciudadana</h4>
            <p className="text-amber-800 text-sm mt-1 leading-relaxed">
                Asegúrate de que tu servidor tenga una conexión a internet estable. Los registros de mensajes fallidos serán guardados automáticamente en tu historial de consumo para propósitos de auditoría.
            </p>
        </div>
      </div>
    </div>
  )
}
